/**
 * Cuisine similarity & cultural proximity — culturally specific edition.
 *
 * Families are keyed by region but their *members* are culturally
 * specific cuisines (e.g. nigerian, ghanaian, senegalese — never
 * "west_african" as a primary identifier). Legacy region codes
 * still work because expandLegacyCuisine() resolves them.
 */

import {
  CULTURES,
  expandLegacyCuisine,
  getMealOriginLabel,
} from './culturalOrigins';

// ----------------------------------------------------------------------
// Cuisine Families (specific members)
// ----------------------------------------------------------------------
export const CUISINE_FAMILIES = {
  west_african_family: {
    core: ['nigerian', 'nigerian_yoruba', 'nigerian_igbo', 'nigerian_hausa', 'ghanaian', 'senegalese', 'ivorian'],
    similar: ['jamaican', 'trinidadian', 'haitian'],
    description: 'West African cuisines',
  },
  east_african_family: {
    core: ['kenyan', 'ethiopian', 'tanzanian', 'ugandan'],
    similar: ['indian_south', 'yemeni'],
    description: 'East African cuisines',
  },
  north_african_family: {
    core: ['moroccan', 'egyptian'],
    similar: ['lebanese', 'turkish', 'greek'],
    description: 'North African cuisines',
  },
  southern_african_family: {
    core: ['south_african'],
    similar: ['kenyan', 'nigerian'],
    description: 'Southern African cuisines',
  },
  caribbean_family: {
    core: ['jamaican', 'trinidadian', 'haitian', 'cuban'],
    similar: ['nigerian', 'ghanaian', 'mexican'],
    description: 'Caribbean cuisines',
  },
  mediterranean_family: {
    core: ['greek', 'italian', 'spanish'],
    similar: ['lebanese', 'turkish', 'moroccan'],
    description: 'Mediterranean cuisines',
  },
  levant_family: {
    core: ['lebanese', 'turkish', 'israeli', 'persian'],
    similar: ['greek', 'egyptian', 'moroccan'],
    description: 'Levantine & Middle Eastern cuisines',
  },
  european_family: {
    core: ['french', 'british', 'german', 'italian', 'spanish'],
    similar: ['american', 'greek'],
    description: 'European cuisines',
  },
  south_asian_family: {
    core: ['indian', 'indian_north', 'indian_south', 'pakistani', 'bangladeshi', 'sri_lankan'],
    similar: ['thai', 'persian'],
    description: 'South Asian cuisines',
  },
  east_asian_family: {
    core: ['chinese', 'japanese', 'korean'],
    similar: ['vietnamese', 'thai'],
    description: 'East Asian cuisines',
  },
  southeast_asian_family: {
    core: ['vietnamese', 'thai', 'filipino', 'indonesian', 'malaysian'],
    similar: ['chinese', 'indian_south'],
    description: 'Southeast Asian cuisines',
  },
  american_family: {
    core: ['american'],
    similar: ['british', 'mexican'],
    description: 'American cuisines',
  },
  latin_american_family: {
    core: ['mexican', 'brazilian', 'peruvian', 'argentinian'],
    similar: ['cuban', 'spanish'],
    description: 'Latin American cuisines',
  },
  modern_global_family: {
    core: ['modern_global', 'general'],
    similar: [],
    description: 'Modern global / fusion',
  },
};

// Map every specific cuisine to its family for O(1) lookups.
export const CUISINE_TO_FAMILY = {};
Object.entries(CUISINE_FAMILIES).forEach(([family, data]) => {
  data.core.forEach((c) => {
    CUISINE_TO_FAMILY[c] = family;
  });
});

// ----------------------------------------------------------------------
// Public helpers
// ----------------------------------------------------------------------

/**
 * Return same-family and similar cuisines for a given cuisine.
 * Accepts both specific cuisines (nigerian) and legacy regions
 * (west_african) — legacy codes are expanded and merged.
 */
export const getSimilarCuisines = (cuisine) => {
  const specifics = expandLegacyCuisine(cuisine);
  const sameFamilySet = new Set();
  const similarSet = new Set();

  specifics.forEach((spec) => {
    const fam = CUISINE_TO_FAMILY[spec];
    if (!fam) return;
    const data = CUISINE_FAMILIES[fam];
    data.core.forEach((c) => {
      // Exclude only the literal input cuisine — keep the rest of the
      // family (legacy "west_african" should still surface "nigerian",
      // "ghanaian" etc. as same-family members).
      if (c !== cuisine) sameFamilySet.add(c);
    });
    (data.similar || []).forEach((c) => similarSet.add(c));
  });

  return {
    same_family: Array.from(sameFamilySet),
    similar: Array.from(similarSet),
  };
};

/**
 * Cultural proximity score (0-100). Identical=100, same family=80,
 * cross-family-but-marked-similar=50, unrelated=20.
 */
export const getCulturalProximity = (cuisine1, cuisine2) => {
  if (cuisine1 === cuisine2) return 100;
  const a = expandLegacyCuisine(cuisine1);
  const b = expandLegacyCuisine(cuisine2);

  // Same family check
  for (const x of a) {
    const xFam = CUISINE_TO_FAMILY[x];
    for (const y of b) {
      if (x === y) return 100;
      const yFam = CUISINE_TO_FAMILY[y];
      if (xFam && xFam === yFam) return 80;
    }
  }

  // Cross-family similar
  for (const x of a) {
    const fam = CUISINE_FAMILIES[CUISINE_TO_FAMILY[x]];
    if (!fam) continue;
    for (const y of b) {
      if ((fam.similar || []).includes(y)) return 50;
    }
  }

  return 20;
};

/**
 * Cultural label rendered on the meal card. Prefers the meal's
 * culturally specific display label, then proximity colouring.
 */
export const getCulturalLabel = (mealOrCuisine, userCuisines = []) => {
  const isObject = typeof mealOrCuisine === 'object' && mealOrCuisine !== null;
  const mealCuisine = isObject ? mealOrCuisine.cuisine : mealOrCuisine;
  const displayText = isObject
    ? getMealOriginLabel(mealOrCuisine)
    : CULTURES[mealCuisine]?.display_label || mealCuisine;

  if (!userCuisines || userCuisines.length === 0) {
    // No user prefs — still surface origin label.
    return {
      text: displayText,
      icon: '🌍',
      color: 'bg-slate-100 text-slate-700 border-slate-200',
      priority: 50,
    };
  }

  const specificUserCuisines = userCuisines.flatMap((c) => expandLegacyCuisine(c));

  if (specificUserCuisines.includes(mealCuisine)) {
    return {
      text: displayText,
      icon: '🏠',
      color: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      priority: 100,
    };
  }

  for (const c of specificUserCuisines) {
    const { same_family, similar } = getSimilarCuisines(c);
    if (same_family.includes(mealCuisine)) {
      return {
        text: displayText,
        icon: '🌐',
        color: 'bg-teal-100 text-teal-700 border-teal-200',
        priority: 80,
      };
    }
    if (similar.includes(mealCuisine)) {
      return {
        text: displayText,
        icon: '🌐',
        color: 'bg-teal-100 text-teal-700 border-teal-200',
        priority: 60,
      };
    }
  }

  return {
    text: displayText,
    icon: '✨',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    priority: 40,
  };
};

// ----------------------------------------------------------------------
// Preference modes (unchanged contract)
// ----------------------------------------------------------------------
export const PREFERENCE_MODES = {
  local_focus: {
    name: 'Local Focus',
    description: 'Prioritize your selected cuisines',
    icon: '🏠',
    distribution: { preferred: 0.80, similar: 0.15, explore: 0.05 },
  },
  balanced_mix: {
    name: 'Balanced Mix',
    description: 'Mix of familiar and diverse options',
    icon: '🌐',
    distribution: { preferred: 0.50, similar: 0.30, explore: 0.20 },
  },
  explore_global: {
    name: 'Explore Global',
    description: 'Discover cuisines from around the world',
    icon: '🗺️',
    distribution: { preferred: 0.30, similar: 0.30, explore: 0.40 },
  },
};

export const DEFAULT_PREFERENCE_MODE = 'local_focus';

/**
 * Cultural-relevance sort used by the meal-grid view.
 */
export const sortByCulturalRelevance = (meals, userCuisines = [], mode = DEFAULT_PREFERENCE_MODE) => {
  const modeConfig = PREFERENCE_MODES[mode] || PREFERENCE_MODES[DEFAULT_PREFERENCE_MODE];

  return meals
    .map((meal) => {
      let culturalScore = 0;
      const label = getCulturalLabel(meal, userCuisines);
      if (label) {
        if (label.priority === 100) culturalScore = 100 * modeConfig.distribution.preferred;
        else if (label.priority >= 60) culturalScore = 80 * modeConfig.distribution.similar;
        else culturalScore = 50 * modeConfig.distribution.explore;
      }
      return { ...meal, culturalScore, culturalLabel: label };
    })
    .sort((a, b) => b.culturalScore - a.culturalScore);
};
